<template>
  <div class="join-page">
    <Sidebar />
    <div class="main-content">
      <!-- 导航和保存按钮 -->
      <div class="header">
        <div class="navigation">
          <span class="nav-item" @click="goToHome">首页</span> / <!-- 使用方法跳转 -->
          <span class="nav-item active">加入我们</span>
        </div>
        <!-- 保存按钮 -->
        <el-button class="save-btn" type="primary" @click="saveContent" :loading="isSaving">
          {{ isSaving ? '保存中...' : '保存内容' }}
        </el-button>
      </div>

      <!-- 编辑区域 -->
      <div class="section editor-section">
        <h3 class="section-title">编辑招聘信息</h3>
        <el-form :model="jobForm" ref="jobFormRef" label-width="100px" label-position="top">
          <!-- label-position="top" 让标签在上方，更适合列表 -->
          <div v-for="(job, index) in jobForm.jobs" :key="job.id || index" class="job-entry">
             <!-- 使用 job.id 作为 key 提高性能，如果后端返回了的话 -->
            <el-form-item :label="`职位 ${index + 1}`" class="job-item-header">
              <el-input v-model="job.title" placeholder="请输入职位名称" clearable></el-input>
            </el-form-item>
            <el-form-item label="职位要求" class="job-item-body">
              <el-input
                type="textarea"
                v-model="job.requirements"
                placeholder="请输入职位要求（每行一条要求）"
                :rows="5"
                resize="vertical"
                show-word-limit
                maxlength="500" 
              ></el-input>
            </el-form-item>
            <div class="job-actions">
               <el-button type="danger" plain @click="removeJob(index)">删除此职位</el-button>
            </div>
          </div>
          <el-button type="success" icon="el-icon-plus" @click="addJob" class="add-job-btn">添加新职位</el-button>
        </el-form>
      </div>

            <!-- 预览区域 -->
            <div class="section preview-section">
        <h3 class="section-title">预览效果</h3>
        <div v-if="loading" class="loading-placeholder">
            <el-skeleton :rows="5" animated />
        </div>
        <div v-else-if="jobForm.jobs.length === 0" class="empty-placeholder">
          暂无招聘信息，请在编辑区添加。
        </div>
        <!-- 网格布局 -->
        <div v-else class="preview-grid">
            <!-- 职位卡片 -->
            <div v-for="(job, index) in jobForm.jobs" :key="'preview-' + (job.id || index)" class="preview-job-card">
              <!-- 卡片头部：职位名称 -->
              <div class="card-header">
                 <i class="fas fa-briefcase card-icon"></i> <!-- 添加图标 -->
                 <h4 class="preview-job-title">{{ job.title || '（未填写标题）' }}</h4>
              </div>

              <!-- 分隔线 -->
              <hr class="card-divider">

              <!-- 卡片主体：职位要求 -->
              <div class="card-body">
                  <h5 class="preview-job-req-title">
                     <i class="fas fa-tasks card-icon-small"></i> <!-- 添加小图标 -->
                     Job requirements:
                  </h5>
                  <ul v-if="job.requirements && job.requirements.trim()" class="preview-job-req-list">
                      <li v-for="(req, reqIndex) in job.requirements.split('\n').filter(r => r.trim())" :key="reqIndex">
                          <i class="fas fa-check-circle req-icon"></i> <!-- 添加列表图标 -->
                          <span>{{ req.trim() }}</span>
                      </li>
                  </ul>
                  <p v-else class="preview-job-req-empty">（未填写具体要求）</p>
              </div>

              <!-- 卡片底部 (可选，可以放联系方式、地点等) -->
              <!--
              <div class="card-footer">
                  <el-button type="primary" size="small">查看详情/申请</el-button>
              </div>
              -->
            </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'; // 移除 computed
import { useRouter } from 'vue-router';
import { ElNotification, ElButton, ElForm, ElFormItem, ElInput, ElSkeleton } from 'element-plus'; // 引入需要的 Element Plus 组件
import Sidebar from '../components/Sidebar.vue'; // 确认路径
import { getJoinus, updateJoinus } from '@/api/cms'; // 从新的 cms.js 导入

// --- 状态定义 ---
const jobForm = reactive({ // 使用 reactive 以便直接修改 jobs 数组
  jobs: [] // 初始化为空数组，将从 API 加载
});
const loading = ref(true); // 初始加载状态设为 true
const isSaving = ref(false); // 保存状态
const router = useRouter();
const jobFormRef = ref(null); // 表单引用 (可选，用于校验)

// --- 默认数据 (仅作为加载失败时的备用) ---
const getDefaultJobs = () => [
  { id: Date.now() + 1, title: '示例职位1', requirements: '要求1\n要求2' },
  { id: Date.now() + 2, title: '示例职位2', requirements: '要求A\n要求B' }
];

// --- 业务逻辑 ---

/** 跳转到首页 */
const goToHome = () => {
  router.push('/');
};

/** 加载内容 */
const loadContent = async () => {
  loading.value = true;
  try {
    const response = await getJoinus(); // 调用 API
    // 假设 response.data 是 { code, message, data: { content: "JSON string" } }
    const savedContent = response?.data?.content; // 安全地访问 content

    if (savedContent) {
      try {
        const parsedJobs = JSON.parse(savedContent);
        // 确保解析结果是数组，并且每个 job 有基本结构
        if (Array.isArray(parsedJobs)) {
          jobForm.jobs = parsedJobs.map(job => ({
            id: job.id || Date.now() + Math.random(), // 确保有 ID
            title: job.title || '',
            requirements: job.requirements || ''
          }));
        } else {
           console.warn('从后端获取的 content 不是有效的职位数组格式, 使用默认值');
           jobForm.jobs = getDefaultJobs();
        }
      } catch (parseError) {
        console.error('解析后端 content 失败:', parseError);
        ElNotification({ title: '警告', message: '加载的内容格式有误，已使用默认数据。', type: 'warning' });
        jobForm.jobs = getDefaultJobs(); // 解析失败使用默认值
      }
    } else {
      // 如果后端没有返回 content，也使用默认值或空数组
      console.log('后端未返回招聘内容，使用默认值');
      jobForm.jobs = getDefaultJobs();
    }
  } catch (error) {
    console.error('获取内容失败:', error);
    // ElMessage 应该在拦截器处理了网络或通用业务错误
    // 这里可以针对性提示加载失败
     ElNotification({
        title: '错误',
        message: `加载招聘信息失败: ${error.message || '请稍后重试'}`,
        type: 'error'
     });
    // 加载失败时不清空当前可能存在的（默认）数据
    // jobForm.jobs = [];
  } finally {
    loading.value = false;
  }
};

/** 保存内容 */
const saveContent = async () => {
  isSaving.value = true;
  try {
    // 过滤掉标题和要求都为空的职位（可选）
    const validJobs = jobForm.jobs.filter(job => job.title?.trim() || job.requirements?.trim());
    const contentToSave = JSON.stringify(validJobs);

    // 只发送 JSON 数据，移除 staticHtml
    await updateJoinus({ content: contentToSave });

    ElNotification({ title: '成功', message: '招聘信息已保存！', type: 'success' });
    // 保存成功后，可以选择重新加载一次以确认数据一致性，或者信任本地数据
    // await loadContent();
  } catch (error) {
    console.error('保存失败:', error);
    // ElMessage 在拦截器处理
     ElNotification({
         title: '错误',
         message: `保存失败: ${error.message || '请稍后重试'}`,
         type: 'error'
     });
  } finally {
    isSaving.value = false;
  }
};

/** 添加新职位 */
const addJob = () => {
  jobForm.jobs.push({
    id: Date.now() + Math.random(), // 添加唯一 ID
    title: '',
    requirements: ''
  });
};

/** 删除职位 */
const removeJob = (index) => {
  if (index >= 0 && index < jobForm.jobs.length) {
    jobForm.jobs.splice(index, 1);
  }
};

// --- 生命周期 ---
onMounted(loadContent); // 页面挂载时加载内容

</script>

<style scoped>
/* 样式基本保持不变，可以根据新的预览结构微调 */
.join-page {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  padding: 30px;
  margin-left: 200px; /* 根据 Sidebar 宽度调整 */
  background: linear-gradient(135deg, #f5f7fa 0%, #e9ecef 100%);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 15px 20px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.navigation {
  display: flex;
  gap: 8px;
  font-size: 16px;
}

.nav-item {
  cursor: pointer;
  color: #555;
  transition: color 0.3s ease;
}

.nav-item.active {
  color: #5a9bd4;
  font-weight: 600;
}

.nav-item:hover:not(.active) { /* 只有非激活项才有悬浮下划线 */
  color: #5a9bd4;
  text-decoration: underline;
}

.save-btn {
  padding: 10px 20px;
}

.section {
  background: #ffffff;
  border-radius: 12px;
  padding: 25px; /* 增加 padding */
  margin-bottom: 30px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}

.editor-section .section-title, /* 统一标题样式 */
.preview-section .section-title {
  margin: 0 0 25px 0; /* 调整标题外边距 */
  padding-bottom: 15px; /* 增加下边距 */
  border-bottom: 1px solid #eee; /* 添加下边框 */
  font-size: 1.1rem; /* 调整字体大小 */
  font-weight: 600;
  color: #333;
}

.job-entry {
  border: 1px solid #e8ecef;
  padding: 20px; /* 增加内边距 */
  margin-bottom: 20px; /* 增加间距 */
  border-radius: 8px;
  background-color: #fdfdfd; /*  हल्का 背景色 */
}
.job-item-header .el-form-item__label { /* 调整职位标题标签样式 */
    font-weight: bold;
    color: #555;
}
.job-item-body .el-form-item__label { /* 调整职位要求标签样式 */
     color: #666;
}
.job-actions {
    text-align: right; /* 删除按钮右对齐 */
    margin-top: 10px;
}

.add-job-btn {
    margin-top: 15px; /* 与上方间隔 */
}

/* --- 预览区样式 --- */
.preview-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr); /* 固定为两列 */
    gap: 30px; /* 增大卡片间距 */
}

/* 响应式调整：在中等屏幕及以下变为单列 */
@media (max-width: 992px) {
  .preview-grid {
    grid-template-columns: 1fr; /* 变为单列 */
    gap: 25px; /* 单列时间距可以小一点 */
  }
}

.preview-job-card {
    background-color: #ffffff; /* 白色背景 */
    border-radius: 10px; /* 更大的圆角 */
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.07); /* 更柔和的阴影 */
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    border: 1px solid #f0f0f0; /* 更浅的边框 */
    overflow: hidden; /* 隐藏内部溢出 */
    display: flex;
    flex-direction: column; /* 垂直布局 */
}
.preview-job-card:hover {
    transform: translateY(-5px); /* 悬浮效果更明显 */
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.card-header {
    padding: 20px 25px; /* 头部内边距 */
    display: flex;
    align-items: center;
    background-color: #f8f9fa; /* 浅灰色头部背景 */
    border-bottom: 1px solid #e9ecef; /* 头部下边框 */
}

.card-icon {
    font-size: 1.4rem; /* 图标大小 */
    color: #5a9bd4; /* 主题色 */
    margin-right: 15px; /* 图标与文字间距 */
    flex-shrink: 0; /* 防止图标被压缩 */
}

.preview-job-title {
    font-size: 1.2rem; /* 增大标题字体 */
    font-weight: 600; /* 加粗 */
    color: #343a40; /* 深灰色 */
    margin: 0; /* 移除默认 margin */
    line-height: 1.4; /* 调整行高 */
}

.card-divider {
    border: none;
    border-top: 1px dashed #dee2e6; /* 虚线分隔线 */
    margin: 0; /* 移除默认 margin */
}

.card-body {
    padding: 25px; /* 主体内边距 */
    flex-grow: 1; /* 占据剩余空间 */
    display: flex; /* 使用 flex 布局 */
    flex-direction: column; /* 垂直排列 */
}

.preview-job-req-title {
    font-size: 1rem; /* 要求标题字体大小 */
    font-weight: 500; /* 中等字重 */
    color: #495057; /* 次深灰色 */
    margin: 0 0 15px 0; /* 调整外边距 */
    display: flex; /* 让图标和文字对齐 */
    align-items: center;
}

.card-icon-small {
    font-size: 0.9rem; /* 小图标大小 */
    color: #6c757d; /* 中灰色 */
    margin-right: 8px; /* 间距 */
}

.preview-job-req-list {
    list-style: none; /* 移除默认列表样式 */
    padding-left: 0; /* 移除默认列表缩进 */
    margin: 0; /* 移除默认 margin */
    font-size: 0.9rem;
    color: #495057; /* 内容颜色 */
    line-height: 1.8; /* 增大行高，更易读 */
    flex-grow: 1; /* 占据剩余空间 */
}
.preview-job-req-list li {
    margin-bottom: 8px; /* 列表项间距 */
    display: flex; /* 使用 flex 对齐图标和文本 */
    align-items: flex-start; /* 图标和多行文本顶部对齐 */
}

.req-icon {
    font-size: 0.8rem; /* 要求列表图标大小 */
    color: #28a745; /* 绿色表示完成或要求 */
    margin-right: 10px; /* 图标与文字间距 */
    margin-top: 5px; /* 微调图标垂直位置，使其与文本对齐 */
    flex-shrink: 0;
}

.preview-job-req-empty {
    font-size: 0.9rem;
    color: #adb5bd; /* 更浅的灰色 */
    font-style: italic;
    text-align: center; /* 居中显示 */
    padding: 20px 0; /* 上下边距 */
    flex-grow: 1;
}

.card-footer { /* 可选的底部样式 */
    padding: 15px 25px;
    background-color: #f8f9fa;
    border-top: 1px solid #e9ecef;
    text-align: right;
}

/* --- 加载和空状态样式 (可以保持不变或微调) --- */
.loading-placeholder,
.empty-placeholder {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
  font-size: 1rem;
}
.loading-placeholder .el-skeleton {
    width: 80%;
    max-width: 400px; /* 限制骨架屏最大宽度 */
    margin: 0 auto;
}

/* 移除之前生成的 HTML 中的 Tailwind 类（如果不再需要） */
/* .container, .mx-auto, .px-4, .py-12, .bg-gray-100, .grid, .grid-cols-1, .md\:grid-cols-2, .gap-8, .bg-white, .p-6, .rounded-lg, .shadow-md, .text-xl, .font-semibold, .text-gray-800, .mb-2, .text-md, .font-medium, .text-gray-600, .mb-4, .list-disc, .list-inside, .space-y-2 { ... } */

</style>